import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userV2API } from '../services/api';
import { ONBOARDING_DESTINATIONS, resolveOnboardingDestination } from '../utils/onboardingFlow';

/**
 * Redirects authenticated users to /onboarding until they satisfy the shared onboarding contract.
 */
export default function OnboardingGate({ children }) {
  const { user, previewMode } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const postOnboardingPath = user?.isAdmin ? '/admin/overview' : '/dashboard/daily-state';

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // previewMode comes from AuthContext, captured once at session
      // establishment -- not re-derived from window.location.search, which
      // ordinary in-app navigation rewrites without the query string. The
      // preview user has no accessToken/refreshToken, so this request would
      // 401 and the axios interceptor would treat it as a real session
      // expiring -- kicking a logged-out visitor straight to the login page
      // before the dashboard, and its preview banner, ever mount. Preview
      // mode has no real onboarding state to check, so let it straight through.
      if (previewMode) {
        setStatus('ready');
        return;
      }

      if (!user?.id) {
        setStatus('ready');
        return;
      }

      try {
        const res = await userV2API.getOnboarding(user.id);
        const onboarding = res.data?.data;
        const steps = onboarding?.completedSteps || [];
        const destination = resolveOnboardingDestination(steps);
        const needsOnboarding = destination !== ONBOARDING_DESTINATIONS.HOME;
        if (!cancelled) {
          setStatus(needsOnboarding ? 'needs-onboarding' : 'ready');
        }
      } catch {
        if (!cancelled) setStatus('ready');
      }
    };

    check();
    return () => { cancelled = true; };
  }, [previewMode, user?.id]);

  if (status === 'loading') return null;
  if (status === 'needs-onboarding' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  if (status === 'ready' && location.pathname === '/onboarding') {
    return <Navigate to={postOnboardingPath} replace />;
  }

  return children;
}
